import React from 'react';
import { Flex, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import {
  OrganizationStorage,
  Storage,
  STORAGE_AREAS,
  StorageArea,
} from '@totem/components/administration/storageReporting/types';
import {
  formatStorage,
  getRecentBytes,
  getStorageFlags,
} from '@totem/components/administration/storageReporting/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { formatBytes } from '@totem/utilities/common';

type StorageRow = {
  key: string;
  name: string;
  storage: Storage;
  totalBytes: number;
  organization?: OrganizationStorage;
};

type Props = {
  organizations: OrganizationStorage[];
  isDeactivated: boolean;
  loading: boolean;
  exportName: string;
};

const MEGABYTE = 1024 * 1024;

const AreaCell = ({ row, area }: { row: StorageRow; area: StorageArea }) => {
  const value = row.storage[area];
  if (value.count === 0) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }

  return (
    <Tooltip
      title={
        <>
          <div>{value.count.toLocaleString()} records</div>
          {value.deletedCount > 0 && (
            <div>
              {value.deletedCount.toLocaleString()} soft-deleted (
              {formatStorage(value.deletedBytes)})
            </div>
          )}
          <div>
            {value.recentCount.toLocaleString()} added in 30 days (
            {formatStorage(value.recentBytes)})
          </div>
        </>
      }
    >
      <div>
        {/* records can hold 0 bytes (e.g. link-only documents) */}
        <div>{formatBytes(value.bytes, 1)}</div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {value.count.toLocaleString()}
        </Typography.Text>
      </div>
    </Tooltip>
  );
};

const getColumns = (
  isDeactivated: boolean,
  isBuildings: boolean,
): ColumnsType<StorageRow> => [
  {
    title: isBuildings ? 'Building' : 'Organization',
    dataIndex: 'name',
    key: 'name',
    sorter: (left, right) => left.name.localeCompare(right.name),
  },
  ...STORAGE_AREAS.map((area) => ({
    title: area.label,
    key: area.key,
    width: 130,
    align: 'right' as const,
    sorter: (left: StorageRow, right: StorageRow) =>
      left.storage[area.key].bytes - right.storage[area.key].bytes,
    render: (_: unknown, row: StorageRow) => (
      <AreaCell row={row} area={area.key} />
    ),
  })),
  {
    title: 'Total',
    dataIndex: 'totalBytes',
    key: 'totalBytes',
    width: 110,
    align: 'right',
    defaultSortOrder: isBuildings ? undefined : 'descend',
    sorter: (left, right) => left.totalBytes - right.totalBytes,
    render: (value: number) => (
      <Typography.Text strong>{formatStorage(value)}</Typography.Text>
    ),
  },
  {
    title: '30-Day Growth',
    key: 'recent',
    width: 120,
    align: 'right',
    sorter: (left, right) =>
      getRecentBytes(left.storage) - getRecentBytes(right.storage),
    render: (_, row) => {
      const recent = getRecentBytes(row.storage);
      return recent > 0 ? `+${formatStorage(recent)}` : '—';
    },
  },
  {
    title: 'Flags',
    key: 'flags',
    width: 190,
    render: (_, row) =>
      row.organization && (
        <Flex wrap gap={4}>
          {getStorageFlags(row.organization, isDeactivated).map((flag) => (
            <Tooltip key={flag.label} title={flag.tooltip}>
              <Tag color={flag.color} style={{ marginInlineEnd: 0 }}>
                {flag.label}
              </Tag>
            </Tooltip>
          ))}
        </Flex>
      ),
  },
];

const toMegabytes = (bytes: number) =>
  Math.round((bytes / MEGABYTE) * 100) / 100;

const exportHeaders = [
  'Organization',
  'Building',
  ...STORAGE_AREAS.flatMap((area) => [
    `${area.label} (MB)`,
    `${area.label} Records`,
  ]),
  'Total (MB)',
  '30-Day Growth (MB)',
  'Soft-Deleted (MB)',
];

const exportRow = (
  organization: string,
  building: string,
  storage: Storage,
  totalBytes: number,
) => [
  organization,
  building,
  ...STORAGE_AREAS.flatMap((area) => [
    toMegabytes(storage[area.key].bytes),
    storage[area.key].count,
  ]),
  toMegabytes(totalBytes),
  toMegabytes(getRecentBytes(storage)),
  toMegabytes(
    STORAGE_AREAS.reduce(
      (total, area) => total + storage[area.key].deletedBytes,
      0,
    ),
  ),
];

const StorageReportTable = ({
  organizations,
  isDeactivated,
  loading,
  exportName,
}: Props) => {
  const rows: StorageRow[] = organizations.map((organization) => ({
    key: organization.organizationId || 'none',
    name: organization.name,
    storage: organization.storage,
    totalBytes: organization.totalBytes,
    organization,
  }));

  const exportRows = organizations.flatMap((organization) => [
    exportRow(
      organization.name,
      '',
      organization.storage,
      organization.totalBytes,
    ),
    ...organization.buildings.map((building) =>
      exportRow(
        organization.name,
        building.name,
        building.storage,
        building.totalBytes,
      ),
    ),
  ]);

  return (
    <IBTable<StorageRow>
      columns={getColumns(isDeactivated, false)}
      dataSource={rows}
      loading={loading}
      rowKey="key"
      pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      expandable={{
        rowExpandable: (row) => (row.organization?.buildings.length ?? 0) > 0,
        expandedRowRender: (row) => (
          <IBTable<StorageRow>
            size="small"
            columns={getColumns(isDeactivated, true).filter(
              (column) => column.key !== 'flags',
            )}
            dataSource={(row.organization?.buildings ?? []).map((building) => ({
              key: building.buildingId || 'none',
              name: building.name,
              storage: building.storage,
              totalBytes: building.totalBytes,
            }))}
            rowKey="key"
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          />
        ),
      }}
      footerLeft={
        <IBTableExportButton
          fileName={exportName}
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default StorageReportTable;
